import React, {
  ReactElement,
  useCallback,
  useState,
  createContext,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '../../../colors';
import {
  formatBalance,
  formatPercentage,
} from '../../../helpers/NumberFormatter';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  selectSideMenuCollapsed,
  toggleSideMenuCollapsed,
} from '../../../store/slices/ui';
import { HiddenValue } from '../../../screens/Overview/Content/ValueHideContext';
import './index.css';

const noop = (): void => undefined;

const SideMenuCollapseContext = createContext<{
  collapsed: boolean;
  toggle: () => void;
}>({
  collapsed: false,
  toggle: noop,
});
export const useSideMenuCollapse = () => useContext(SideMenuCollapseContext);

/* The Current / Total metric is owned by the SideMenu and read by every row.
 * `current` → the running profit passed as `delta`; `total` → the realized
 * stable profit passed as `deltaTotal`. Rows without a `deltaTotal` ignore it. */
type SideMenuMetric = 'current' | 'total';
const SideMenuMetricContext = createContext<{
  metric: SideMenuMetric;
  setMetric: (m: SideMenuMetric) => void;
}>({
  metric: 'current',
  setMetric: noop,
});
export const useSideMenuMetric = () => useContext(SideMenuMetricContext);

export const SideMenu = ({
  hasPositiveProfit,
  children,
}: {
  /** Tints the sidebar with a subtle green/red wash based on the active
   *  cluster / subscription profit state. Undefined → pure black. */
  hasPositiveProfit?: boolean;
  children: ReactElement | Array<ReactElement | undefined> | undefined;
}) => {
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSideMenuCollapsed);
  const toggle = useCallback(() => {
    dispatch(toggleSideMenuCollapsed());
  }, [dispatch]);

  const [metric, setMetric] = useState<SideMenuMetric>('current');

  const profitClass =
    typeof hasPositiveProfit === 'undefined'
      ? ''
      : hasPositiveProfit
      ? ' side-menu-positive'
      : ' side-menu-negative';

  return (
    <SideMenuCollapseContext.Provider value={{ collapsed, toggle }}>
      <SideMenuMetricContext.Provider value={{ metric, setMetric }}>
        <div
          className={`side-menu-container${
            collapsed ? ' side-menu-collapsed' : ''
          }${profitClass}`}
        >
          <button
            className="side-menu-collapse-btn"
            onClick={toggle}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? '›' : '‹'}
          </button>
          <div className="side-menu-content">{children}</div>
        </div>
      </SideMenuMetricContext.Provider>
    </SideMenuCollapseContext.Provider>
  );
};

export const SideMenuSection = ({ children }: { children: any }) => {
  return <div className="side-menu-section">{children}</div>;
};

/* Underline switch — two mono labels with a 1px indicator that slides
 * between them. Mirrors the chart timeframe-pill "selected" affordance. */
const MetricToggle = () => {
  const { metric, setMetric } = useSideMenuMetric();
  const isCurrent = metric === 'current';

  return (
    <div className="side-menu-toggle">
      {(
        [
          ['current', 'Current'],
          ['total', 'Total'],
        ] as Array<[SideMenuMetric, string]>
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => setMetric(value)}
          className={`side-menu-toggle-btn${
            metric === value ? ' is-active' : ''
          }`}
        >
          {label}
        </button>
      ))}
      <div className="side-menu-toggle-track" />
      <div
        className="side-menu-toggle-indicator"
        style={{ left: isCurrent ? '0%' : '50%' }}
      />
    </div>
  );
};

export const SideMenuTitle = ({
  title,
  count,
  toggle,
}: {
  title: string;
  /** Optional "· N" badge after the label. */
  count?: number;
  /** Renders the Current / Total metric toggle under the label. */
  toggle?: boolean;
}) => {
  return (
    <div className="side-menu-section-title-container">
      <h3 className="side-menu-section-title">
        <span>{title}</span>
        {typeof count === 'number' && (
          <span className="side-menu-section-title-count">· {count}</span>
        )}
      </h3>
      {toggle && <MetricToggle />}
    </div>
  );
};

export const SideMenuSubTitle = ({
  title,
  count,
}: {
  title: string;
  count?: number;
}) => {
  return (
    <div className="side-menu-section-subtitle-container">
      <h3 className="side-menu-section-subtitle">
        {title}
        {typeof count === 'number' && (
          <span className="side-menu-section-title-count"> · {count}</span>
        )}
      </h3>
    </div>
  );
};

type RowVariant = 'big' | 'small' | 'metric' | 'nav';

/* Heatmap tint: alpha scales with |delta| (a 0..1 fraction), red below
 * zero, green above. Matches the design's 0.15 → 1.0 intensity ramp. */
const railTintFor = (delta?: number): string => {
  if (typeof delta !== 'number') return 'rgba(255,255,255,0.08)';
  const t = Math.min(1, Math.abs(delta));
  const alpha = (0.15 + t * 0.85).toFixed(2);
  if (delta < 0) return `rgba(240,22,47,${alpha})`;
  if (delta > 0) return `rgba(0,176,97,${alpha})`;
  return 'rgba(255,255,255,0.08)';
};

const RAIL_HEIGHT: Record<RowVariant, number> = {
  big: 44,
  small: 30,
  metric: 24,
  nav: 18,
};

export const SideMenuItem = ({
  id,
  label,
  value,
  delta,
  deltaTotal,
  big,
  onClick,
  isActive,
  metricValue,
}: {
  id: number | string;
  label: string;
  value?: number;
  delta?: number;
  /** The "Total" metric value, surfaced when the section toggle is on Total. */
  deltaTotal?: number;
  /** Promotes the row to the large cluster style (Kanit name + big value). */
  big?: boolean;
  onClick?: (id: any) => void;
  isActive?: boolean;
  /**
   * When provided on a metric-variant row, renders this string on the right
   * instead of the delta percentage. Accent rail color still derives from delta.
   * Has no effect on non-metric rows.
   */
  metricValue?: string;
}) => {
  const { metric } = useSideMenuMetric();
  const effectiveDelta =
    metric === 'total' && typeof deltaTotal === 'number' ? deltaTotal : delta;

  const hasValue = typeof value === 'number' && !!value;
  const hasDelta = typeof effectiveDelta === 'number';
  const clickable = !!onClick;

  const variant: RowVariant = big
    ? 'big'
    : hasValue
    ? 'small'
    : hasDelta
    ? 'metric'
    : 'nav';

  // Rail: nav rows use a neutral white/faint rail; data rows are tinted.
  const rail =
    variant === 'nav'
      ? {
          background: isActive ? colors.white : 'rgba(255,255,255,0.18)',
          opacity: isActive ? 1 : 0.5,
        }
      : {
          background: railTintFor(effectiveDelta),
          opacity: isActive ? 1 : 0.85,
        };

  const deltaColor =
    !hasDelta || effectiveDelta === 0
      ? colors.white
      : (effectiveDelta as number) > 0
      ? colors.green
      : colors.red;

  const nameClass =
    variant === 'big'
      ? 'side-menu-name--big'
      : variant === 'small'
      ? 'side-menu-name--pair'
      : variant === 'metric'
      ? 'side-menu-name--metric'
      : 'side-menu-name--nav';

  return (
    <div
      onClick={() => onClick?.(id)}
      className={`side-menu-section-item side-menu-section-item--${variant}${
        clickable ? ' is-clickable' : ''
      }${isActive ? ' is-active' : ''}`}
    >
      <div
        className="side-menu-rail"
        style={{ height: RAIL_HEIGHT[variant], ...rail }}
      />

      <div className="side-menu-row-text">
        <h4 className={`side-menu-name ${nameClass}`}>{label}</h4>
        {hasValue && (
          <h5
            className={`side-menu-value side-menu-value--${
              variant === 'big' ? 'big' : 'small'
            }`}
          >
            <HiddenValue>{formatBalance(value as number, true)}</HiddenValue>
          </h5>
        )}
      </div>

      {hasDelta && (
        <h5
          className={`side-menu-delta side-menu-delta--${
            variant === 'big' ? 'big' : 'small'
          }`}
          style={{
            color:
              variant === 'metric' && metricValue != null
                ? colors.white
                : deltaColor,
          }}
        >
          {variant === 'metric' && metricValue != null
            ? metricValue
            : formatPercentage(effectiveDelta as number, 2)}
        </h5>
      )}
    </div>
  );
};

export const SideMenuSelect = ({
  activeItem,
  options,
}: {
  activeItem: ReactElement;
  options: ReactElement[];
}) => {
  // Always display the activeItem, render options when clicked
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <div className="side-menu-select-head" onClick={() => setIsOpen(!isOpen)}>
        {activeItem}
        {isOpen ? (
          <FaChevronUp color={colors.white} size={20} />
        ) : (
          <FaChevronDown color={colors.gray} size={20} />
        )}
      </div>

      <div
        style={{
          maxHeight: isOpen ? 500 : 0,
          transition: 'max-height 0.5s ease-out',
        }}
        className="side-menu-section-options"
      >
        {options?.map((o, i) => (
          <div key={`options_item_${i}`} onClick={() => setIsOpen(false)}>
            {o}
          </div>
        ))}
      </div>
    </div>
  );
};
